import { useTenderDeadline } from "../../../../../hooks/useTenderDeadline";
import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment";
import cn from "classnames";
import style from "./ActionsTab.module.scss";
import { File, Order } from "../../../../../services";
import SuggestPriceExpeditor from "../SuggestPriceExpeditor/SuggestPriceExpeditor";
import ComplexOrderBids from "../ComplexOrderBids/ComplexOrderBids";
import OrderBidBlock from "../OrderBidBlock/OrderBidBlock";
import ComplexOrderBidBlock from "../ComplexOrderBidBlock/ComplexOrderBidBlock";
import ExpectedArrivalBlock from "../ExpectedArrivalBlock/ExpectedArrivalBlock";
import { shouldBlockStatusChangeForMissingExpectedArrival } from "../ExpectedArrivalBlock/expectedArrivalValidation";
import CurrentLocationBlock from "../CurrentLocationBlock/CurrentLocationBlock";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import ConfirmActionModal from "../ConfirmActionModal/ConfirmActionModal";
import AssignDriverModal from "../AssignDriverModal/AssignDriverModal";
import ClockIcon from "../../../../shared/icons/ClockIcon";
import CheckIcon from "../../../../shared/icons/CheckIcon";
import CanceledOrderCard from "../CanceledOrderCard/CanceledOrderCard";
import ExpeditorRejectedOrderCard from "../ExpeditorRejectedOrderCard/ExpeditorRejectedOrderCard";
import MultiPointStatusCard from "../MultiPointStatusCard/MultiPointStatusCard";
import IntermediateRouteCard from "../IntermediateRouteCard/IntermediateRouteCard";
import { isRailwayOrder } from "../../../../../pages/orders/utils/railwayOrder";
import InsuranceBlock from "../InsuranceBlock/InsuranceBlock";
import WagonNumbersBlock from "../WagonNumbersBlock/WagonNumbersBlock";
import { hasCompleteWagonNumbers } from "../WagonNumbersBlock/wagonNumbersValidation";

const formatPrice = (value, currency = "KZT") => {
  if (value === null || value === undefined) return "—";
  const formatted = Number(value).toLocaleString("ru-RU").replace(/,/g, " ");
  const symbol = currency === "KZT" ? "₸" : currency;
  return `${formatted} ${symbol}`;
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  return moment(iso).locale("ru").format("DD.MM.YYYY, HH:mm");
};

const ActionsTab = ({
  order,
  cargoDetails,
  statusHistory = [],
  fetchOrderData,
  onForceUpdate,
  refreshDocuments,
  onClose,
  previousBids = [],
  bidsLoaded = false,
  fetchBids,
  onlyBid = false,
  onViewRoute,
  onWagonNumbersSaved,
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(order?.tenderDateEnd);
  const { t } = useTranslation();
  const isRailway = isRailwayOrder(order);
  const company = useSelector((s) => s.user.company);
  const { user } = useSelector((state) => state);

  const [docName, setDocName] = useState("");
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null, title: "", message: "" });
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [expectedArrivalError, setExpectedArrivalError] = useState({ addressId: null, focusRequest: 0 });
  const [isSavingWagonNumbers, setIsSavingWagonNumbers] = useState(false);
  const [wagonNumbersValidation, setWagonNumbersValidation] = useState({ key: null, attempt: 0 });

  const status = (order?.status || "").toLowerCase();
  const requiresWagonNumbers = isRailway && status === "confirmed" && Number(order?.wagonsCount) > 0;
  const wagonNumbersKey = `${order?.id}:${order?.wagonsCount}`;
  const wagonNumbersValidationRequest =
    wagonNumbersValidation.key === wagonNumbersKey ? wagonNumbersValidation.attempt : 0;
  const hasMissingWagonNumbers =
    requiresWagonNumbers &&
    !hasCompleteWagonNumbers(Number(order.wagonsCount), order.wagonNumbers);
  const isOwnCompany = order?.companyId === company?.id;
  const userCompanyId = user?.companyId ?? company?.id;
  const isAssignedExpeditor =
    userCompanyId != null && order?.expeditorId != null && String(order.expeditorId) === String(userCompanyId);
  const isOtherExpeditorAssigned = !!order?.expeditorId && !isAssignedExpeditor;
  const showBidBlock =
    bidsLoaded && previousBids.length > 0 && (status === "awaitingoffer" || !isAssignedExpeditor);
  const currentAddress = useMemo(() => order?.addressList?.find((a) => !a.isCompleted) || null, [order]);
  const hasExpectedArrivalError =
    expectedArrivalError.focusRequest > 0 &&
    expectedArrivalError.addressId === currentAddress?.id &&
    !currentAddress?.expectedUnloadingDate;
  const hasIntermediatePoints = (order?.addressList?.length || 0) > 2;
  const isLastPoint = (order?.addressList || []).filter((address) => !address.isCompleted).length <= 1;
  const statusAfterPointCompletion = isLastPoint
    ? order?.type === 1 && isOwnCompany
      ? "completed"
      : "completedbyexecutor"
    : "onthetrip";
  const isCanceled = status === "canceled";
  const canceledStatus = useMemo(
    () =>
      [...(Array.isArray(statusHistory) ? statusHistory : [])]
        .reverse()
        .find((item) => item.statusCode?.toLowerCase() === "canceled") || null,
    [statusHistory],
  );
  const canceledComment = canceledStatus?.comment || "";
  const canceledText = canceledComment
    ? `${t("orderInfoExpeditor.history.statusCanceledByPartner")}. ${t(
        "orderInfoExpeditor.history.priceChangedReason",
      )} ${canceledComment}`
    : t("orderInfoExpeditor.history.statusCanceledByPartner");

  const openConfirm = (title, message, action) => {
    setConfirmModal({ open: true, action, title, message });
  };

  const closeConfirm = () => setConfirmModal({ open: false, action: null, title: "", message: "" });

  const handleConfirm = async () => {
    const action = confirmModal.action;
    closeConfirm();
    if (action) await action();
  };

  const updateStatus = async (nextStatus, successMessage) => {
    if (!order?.id) return;
    setStatusLoading(true);
    try {
      const isCompletingPoint = status === "processloadingcargo" || status === "processunloadingcargo";
      const addressId = isCompletingPoint && nextStatus !== status ? currentAddress?.id || "" : "";
      await Order.updateExpeditorStatus(order.id, nextStatus, addressId);
      toast.success(successMessage);
      if (fetchOrderData) await fetchOrderData();
      if (onForceUpdate) onForceUpdate();
    } catch (error) {
      console.error(error);
      toast.error(t("common.genericError"));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleAccept = () => {
    const nextStatus = order?.isWithoutExecutor ? "confirmed" : "assignedexecutor";
    openConfirm(
      t("orderInfoExpeditor.actions.acceptOrderConfirmTitle"),
      t("orderInfoExpeditor.actions.acceptTripMessage"),
      () => updateStatus(nextStatus, t("orderInfoExpeditor.actions.orderAccepted")),
    );
  };

  const handleReject = () => {
    openConfirm(
      t("orderInfoExpeditor.actions.rejectOrderConfirmTitle"),
      t("orderInfoExpeditor.actions.rejectTripMessage"),
      () => updateStatus("expeditorrejected", t("orderInfoExpeditor.actions.orderRejected")),
    );
  };

  const showExpectedArrivalError = () => {
    toast.error(t("orderInfoExpeditor.expectedArrival.requiredBeforeStatusChange"));
    setExpectedArrivalError((previous) => ({
      addressId: currentAddress?.id,
      focusRequest: previous.focusRequest + 1,
    }));
  };

  const handleStartLoading = () => {
    if (isSavingWagonNumbers) return;
    if (hasMissingWagonNumbers) {
      setWagonNumbersValidation((previous) => ({
        key: wagonNumbersKey,
        attempt: previous.key === wagonNumbersKey ? previous.attempt + 1 : 1,
      }));
      toast.error(t("orderInfoExpeditor.wagonNumbers.required"));
      return;
    }
    if (
      shouldBlockStatusChangeForMissingExpectedArrival({
        status,
        companyId: order?.companyId,
        currentAddress,
      })
    ) {
      showExpectedArrivalError();
      return;
    }

    openConfirm(
      t("orderInfoExpeditor.actions.startLoadingConfirmTitle"),
      t("orderInfoExpeditor.actions.statusChangeConfirm"),
      () => updateStatus("processloadingcargo", t("orderInfoExpeditor.actions.loadingStartedSuccess")),
    );
  };

  const handleOnTheTrip = () => {
    openConfirm(
      t("orderInfoExpeditor.actions.completeLoadingConfirmTitle"),
      t("orderInfoExpeditor.actions.statusChangeConfirm"),
      () =>
        updateStatus(
          statusAfterPointCompletion,
          t(
            isLastPoint
              ? "orderInfoExpeditor.actions.loadingCompleted"
              : isRailway
                ? "orderInfoExpeditor.actions.driverStartedTripRailway"
                : "orderInfoExpeditor.actions.driverStartedTrip",
          ),
        ),
    );
  };

  const handleStartUnloading = () => {
    if (
      shouldBlockStatusChangeForMissingExpectedArrival({
        status,
        companyId: order?.companyId,
        currentAddress,
      })
    ) {
      showExpectedArrivalError();
      return;
    }

    openConfirm(
      t("orderInfoExpeditor.actions.startUnloadingConfirmTitle"),
      t("orderInfoExpeditor.actions.statusChangeConfirm"),
      () =>
        updateStatus("processunloadingcargo", t("orderInfoExpeditor.actions.unloadingStartedSuccess")),
    );
  };

  const handleCompleteUnloading = () => {
    openConfirm(t("common.confirmAction"), t("orderInfoExpeditor.actions.statusChangeConfirm"), () => {
      const successMessage = isLastPoint
        ? t("orderInfoExpeditor.actions.unloadingCompleted")
        : t("orderInfoExpeditor.actions.nextPointTransition");
      return updateStatus(statusAfterPointCompletion, successMessage);
    });
  };

  const handleChooseFile = () => {
    if (!docName.trim()) {
      toast.error(t("orderInfo.documentsTab.docNameRequired"));
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !order?.id) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("FileName", file.name);
    formData.append("orderId", order.id);
    formData.append("name", docName.trim());
    formData.append("Name", docName.trim());
    formData.append("type", 5);

    setIsUploadingDoc(true);
    File.addOrderDoc(formData)
      .then(() => {
        toast.success(t("orderInfo.documentsTab.uploadSuccess"));
        setDocName("");
        if (refreshDocuments) refreshDocuments();
      })
      .catch(() => toast.error(t("orderInfo.documentsTab.uploadError")))
      .finally(() => setIsUploadingDoc(false));
  };

  const handleBidUpdate = () => {
    if (fetchBids) fetchBids();
    if (onForceUpdate) onForceUpdate();
  };

  const renderSuggestPriceBlock = () => {
    if (isOtherExpeditorAssigned) return null;
    if (status !== "awaitingoffer") return null;
    if (!bidsLoaded) return null;
    if (previousBids.length > 0) return null;
    if (order?.tenderDateEnd && isTenderExpiredByServer) return null;

    if (order?.isComplexTender) {
      return (
        <ComplexOrderBids
          onRefreshOrder={fetchOrderData}
          order={order}
          onUpdateData={handleBidUpdate}
          onReject={onClose}
        />
      );
    }

    return (
      <SuggestPriceExpeditor
        onRefreshOrder={fetchOrderData}
        order={order}
        cargoDetails={cargoDetails}
        onUpdateData={handleBidUpdate}
        onReject={onClose}
      />
    );
  };

  const renderOrderBidBlock = () => {
    if (!showBidBlock) return null;

    const BidBlock = order?.isComplexTender ? ComplexOrderBidBlock : OrderBidBlock;

    return (
      <BidBlock
        onRefreshOrder={fetchOrderData}
        bid={previousBids[0]}
        order={order}
        isCanceled={isCanceled}
        canceledText={canceledText}
        onUpdateData={handleBidUpdate}
        onFindNewOrders={onClose}
      />
    );
  };

  const renderCanceledBlock = () => {
    if (!isCanceled) return null;
    if (!bidsLoaded) return null;
    if (showBidBlock) return null;

    return <CanceledOrderCard canceledStatus={canceledStatus} onFindNewOrders={onClose} />;
  };

  const renderDocsUploadBlock = () => {
    if (status === "completed" || status === "expeditorrejected" || isCanceled) return null;

    return (
      <div className={style.card}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>{t("orderInfoExpeditor.actions.attachDocuments")}</span>
        </div>
        <div className={style.docs_row}>
          <div className={style.docs_input_wrap}>
            <CustomInput
              value={docName}
              isBgGray
              labelText={t("orderInfo.documentsTab.docNameLabel")}
              placeholder={t("orderInfo.documentsTab.docNamePlaceholder")}
              onChangeValue={setDocName}
            />
          </div>
          <div className={style.docs_file_btn}>
            <CustomButton
              text={t("orderInfoExpeditor.actions.chooseFile")}
              isNavy
              onClick={handleChooseFile}
              isLoading={isUploadingDoc}
              disabled={isUploadingDoc}
            />
          </div>
        </div>
        <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFileSelected} />
      </div>
    );
  };

  const renderAssignedBlock = () => {
    if (status !== "assigned") return null;

    return (
      <div className={cn(style.card, style.card_compact)}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>{t("orderInfoExpeditor.actions.partnerAssignedTrip")}</span>
          <span className={style.topbar_meta}>{t("orderInfoExpeditor.actions.amountFixed")}</span>
        </div>
        <div className={style.amount_row}>
          <span className={style.amount_label}>{t("orderInfoExpeditor.actions.amount")}</span>
          <span className={style.amount_value}>{formatPrice(order?.price, order?.currency)}</span>
        </div>
        <div className={style.actions_row}>
          <div className={style.actions_row_primary}>
            <CustomButton
              text={t("orderInfo.orderDecision.acceptButton")}
              isNavy
              isFall
              onClick={handleAccept}
              isLoading={statusLoading}
              disabled={statusLoading}
            />
          </div>
          <div className={style.actions_row_secondary}>
            <CustomButton
              text={t("orderInfo.orderDecision.rejectButton")}
              isNavy
              isWhite
              onClick={handleReject}
              disabled={statusLoading}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderAssignExecutorBlock = () => {
    if (status !== "assignedexecutor") return null;
    return (
      <div className={style.card}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>{t("orderInfoExpeditor.actions.selectExecutorTitle")}</span>
        </div>
        <div className={style.actions_row_full}>
          <CustomButton
            text={t("orderInfoExpeditor.actions.assignExecutor")}
            isNavy
            onClick={() => setAssignModalOpen(true)}
            style={{ width: "100%" }}
          />
        </div>
      </div>
    );
  };

  const renderWagonNumbersBlock = () =>
    requiresWagonNumbers ? (
      <WagonNumbersBlock
        key={wagonNumbersKey}
        order={order}
        validationRequest={wagonNumbersValidationRequest}
        disabled={statusLoading}
        onSavingChange={setIsSavingWagonNumbers}
        onSaved={(orderId, wagonNumbers) => {
          if (onWagonNumbersSaved) onWagonNumbersSaved(orderId, wagonNumbers);
          else if (fetchOrderData) fetchOrderData({ silent: true });
          if (onForceUpdate) onForceUpdate();
        }}
      />
    ) : null;

  const renderWaitingArrivalBlock = () => {
    const isInterimLoading = status === "onthetrip" && currentAddress?.type === "Loading";
    const showBlock = status === "confirmed" || status === "headingtoloading" || isInterimLoading;
    if (!showBlock) return null;

    return (
      <div className={cn(style.card, style.card_compact)}>
        {requiresWagonNumbers ? (
          renderWagonNumbersBlock()
        ) : (
          <div className={style.topbar}>
            <span className={style.topbar_title}>
              {t(
                isRailway
                  ? "orderInfoExpeditor.actions.confirmLoadingArrivalTitleRailway"
                  : "orderInfoExpeditor.actions.confirmLoadingArrivalTitle",
              )}
            </span>
          </div>
        )}
        <div className={style.actions_row_full}>
          <CustomButton
            text={t("orderInfo.orderDecision.startLoadingButton")}
            isNavy
            onClick={handleStartLoading}
            isLoading={statusLoading}
            disabled={statusLoading || isSavingWagonNumbers}
            style={{ width: "100%" }}
          />
        </div>
      </div>
    );
  };

  const renderOnTheWayBlock = () => {
    if (status !== "processloadingcargo") return null;
    return (
      <div className={style.card}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>
            {t(
              isLastPoint
                ? "orderInfoExpeditor.actions.completeLoadingConfirmTitle"
                : isRailway
                  ? "orderInfoExpeditor.actions.loadingInProgressTitleRailway"
                  : "orderInfoExpeditor.actions.loadingInProgressTitle",
            )}
          </span>
        </div>
        <div className={style.actions_row_full}>
          <CustomButton
            text={t(
              isLastPoint
                ? "orderInfoExpeditor.actions.completeLoadingButton"
                : "orderInfoExpeditor.actions.startTrip",
            )}
            isNavy
            onClick={handleOnTheTrip}
            isLoading={statusLoading}
            disabled={statusLoading}
            style={{ width: "100%" }}
          />
        </div>
      </div>
    );
  };

  const renderWaitingUnloadingBlock = () => {
    const isInterimUnloading = status === "onthetrip" && currentAddress?.type === "Unloading";
    if (!isInterimUnloading) return null;

    return (
      <div className={cn(style.card, style.card_compact)}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>
            {t(
              isRailway
                ? "orderInfoExpeditor.actions.waitingUnloadingArrivalTitleRailway"
                : "orderInfoExpeditor.actions.waitingUnloadingArrivalTitle",
            )}
          </span>
        </div>
        <div className={style.actions_row_full}>
          <CustomButton
            text={t("orderInfo.orderDecision.startUnloadingButton")}
            isNavy
            onClick={handleStartUnloading}
            isLoading={statusLoading}
            disabled={statusLoading}
            style={{ width: "100%" }}
          />
        </div>
      </div>
    );
  };

  const renderUnloadingModerationBlock = () => {
    if (status !== "unloadingcargomoderation") return null;

    const handleConfirmUnloadingModeration = () => {
      openConfirm(
        t("common.confirmAction"),
        t("orderInfoExpeditor.actions.confirmUnloadingModerationConfirm"),
        () => {
          const nextStatus = order?.type === 1 && isOwnCompany ? "completed" : "completedbyexecutor";
          return updateStatus(nextStatus, t("orderInfoExpeditor.actions.unloadingModerationConfirmed"));
        },
      );
    };

    const handleRejectUnloadingModeration = () => {
      openConfirm(
        t("common.confirmAction"),
        t("orderInfoExpeditor.actions.rejectUnloadingModerationConfirm"),
        () => updateStatus("onthetrip", t("orderInfoExpeditor.actions.unloadingModerationRejected")),
      );
    };

    return (
      <div className={cn(style.card, style.card_compact)}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>
            {t("orderInfoExpeditor.actions.unloadingModerationTitle")}
          </span>
        </div>
        <div className={style.actions_row}>
          <div className={style.actions_row_primary}>
            <CustomButton
              text={t("orderInfo.orderDecision.confirmButton")}
              isNavy
              isFall
              onClick={handleConfirmUnloadingModeration}
              isLoading={statusLoading}
              disabled={statusLoading}
            />
          </div>
          <div className={style.actions_row_secondary}>
            <CustomButton
              text={t("orderInfo.orderDecision.rejectButton")}
              isNavy
              isWhite
              onClick={handleRejectUnloadingModeration}
              disabled={statusLoading}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderUnloadingBlock = () => {
    if (status !== "processunloadingcargo") return null;
    return (
      <div className={cn(style.card, style.card_compact)}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>
            {t("orderInfoExpeditor.actions.unloadingInProgressTitle")}
          </span>
        </div>
        <div className={style.actions_row_full}>
          <CustomButton
            text={t("orderInfo.orderDecision.completeUnloadingButton")}
            isNavy
            onClick={handleCompleteUnloading}
            isLoading={statusLoading}
            disabled={statusLoading}
            style={{ width: "100%" }}
          />
        </div>
      </div>
    );
  };

  const renderReturnBlock = () => {
    if (status !== "return") return null;

    const handleResubmit = () => {
      openConfirm(
        t("orderInfoExpeditor.actions.resubmitConfirmTitle"),
        t("orderInfoExpeditor.actions.resubmitConfirmMessage"),
        () => updateStatus("completedbyexecutor", t("orderInfoExpeditor.actions.resubmittedToModeration")),
      );
    };

    return (
      <div className={style.card}>
        <div className={style.topbar}>
          <span className={style.topbar_title}>{t("orderInfoExpeditor.actions.returnFixTitle")}</span>
        </div>
        <div className={style.comment_box}>
          <div className={style.comment_label_row}>
            <span className={style.comment_label}>{t("orderInfoExpeditor.actions.partnerCommentLabel")}</span>
          </div>
          <span className={style.comment_text}>{order?.rejectReason ? order.rejectReason : "-"}</span>
        </div>
        <div className={style.actions_row_full}>
          <CustomButton
            text={t("orderInfoExpeditor.actions.resubmitToPartner")}
            isNavy
            onClick={handleResubmit}
            isLoading={statusLoading}
            disabled={statusLoading}
            style={{ width: "100%" }}
          />
        </div>
      </div>
    );
  };

  const renderModerationBlock = () => {
    if (status !== "completedbyexecutor") return null;
    if (isOwnCompany) return null;

    const senderCompanyName = order?.senderCompanyName || t("orderInfoExpeditor.actions.shipperFallback");
    return (
      <div className={style.info_card}>
        <div className={cn(style.info_icon_circle, style.info_icon_warning)}>
          <ClockIcon type={2} fill="#FF9800" />
        </div>
        <div className={style.info_text_group}>
          <span className={style.info_title_secondary}>
            {t("orderInfoExpeditor.actions.shipperChecksDocuments", { company: senderCompanyName })}
          </span>
          <span className={style.info_subtitle}>
            <span className={style.info_subtitle_strong}>{t("orderInfoExpeditor.actions.averageTime")} </span>
            <span className={style.info_subtitle_accent}>
              {t("orderInfoExpeditor.actions.averageTimeValue")}
            </span>
          </span>
        </div>
      </div>
    );
  };

  const renderCompletedBlock = () => {
    if (status !== "completed") return null;
    return (
      <div className={style.info_card}>
        <div className={cn(style.info_icon_circle, style.info_icon_success)}>
          <CheckIcon color="#ffffff" width="72px" height="78px" />
        </div>
        <div className={style.info_text_group}>
          <span className={style.info_title_primary}>{t("orderInfoExpeditor.actions.orderCompleted")}</span>
          <span className={style.info_subtitle}>
            {t("orderInfoExpeditor.actions.partnerAcceptedAt", { date: formatDateTime(order?.updatedAt) })}
          </span>
        </div>
      </div>
    );
  };

  if (!order) return null;
  if (status === "expeditorrejected" && isAssignedExpeditor) {
    return (
      <div className={style.wrapper}>
        {!onlyBid && <InsuranceBlock order={order} refreshDocuments={refreshDocuments} />}
        <ExpeditorRejectedOrderCard
          history={statusHistory}
          updatedAt={order.updatedAt}
          onFindNewOrders={onClose}
        />
      </div>
    );
  }
  if (onlyBid) return renderOrderBidBlock();

  return (
    <div className={style.wrapper}>
      {hasIntermediatePoints && (status === "awaitingoffer" || status === "assigned") && (
        <IntermediateRouteCard addresses={order.addressList} onViewRoute={onViewRoute} />
      )}
      {renderSuggestPriceBlock()}
      {renderOrderBidBlock()}
      {renderCanceledBlock()}
      {!isOtherExpeditorAssigned && <InsuranceBlock order={order} refreshDocuments={refreshDocuments} />}
      {!isOtherExpeditorAssigned && (
        <>
          {renderAssignedBlock()}
          {renderAssignExecutorBlock()}
          {hasIntermediatePoints ? (
            <MultiPointStatusCard
              addresses={order.addressList}
              currentAddress={currentAddress}
              status={status}
              isLastPoint={isLastPoint}
              isLoading={statusLoading}
              onStartLoading={handleStartLoading}
              onCompleteLoading={handleOnTheTrip}
              onStartUnloading={handleStartUnloading}
              onCompleteUnloading={handleCompleteUnloading}
              isActionDisabled={isSavingWagonNumbers}
            >
              {renderWagonNumbersBlock()}
            </MultiPointStatusCard>
          ) : (
            <>
              {renderWaitingArrivalBlock()}
              {renderOnTheWayBlock()}
              {renderWaitingUnloadingBlock()}
              {renderUnloadingBlock()}
            </>
          )}
          {renderUnloadingModerationBlock()}
          {renderReturnBlock()}
        </>
      )}
      <ExpectedArrivalBlock
        key={currentAddress?.id}
        order={order}
        fetchOrderData={fetchOrderData}
        addressId={currentAddress?.id || null}
        initialUnloadingDate={currentAddress?.expectedUnloadingDate || null}
        errorText={hasExpectedArrivalError ? t("orderInfoExpeditor.expectedArrival.requiredBeforeStatusChange") : ""}
        focusRequest={hasExpectedArrivalError ? expectedArrivalError.focusRequest : 0}
      />
      <CurrentLocationBlock order={order} />
      {!isOtherExpeditorAssigned && <>{renderDocsUploadBlock()}</>}
      {renderModerationBlock()}
      {renderCompletedBlock()}

      <ConfirmActionModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
        isLoading={statusLoading}
      />

      <AssignDriverModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        order={order}
        mode="add"
        onSuccess={() => {
          if (fetchOrderData) fetchOrderData();
          if (onForceUpdate) onForceUpdate();
        }}
      />
    </div>
  );
};

export default ActionsTab;
